import React from 'react';
import { NextUI } from '@/components/NextUI';
import FormCard from '../../../../components/Layouts/Default/FormCard';
import Button from '@/components/NextUI/Button';
import { useAppDispatch } from '@/hooks/redux';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { showToast } from '@/store/slices/toastActions';
import useAuth from '@/hooks/useAuth';
import { usePlan } from '@/features/subscription/hooks/usePlan';
import { useTranslations } from 'next-intl';

const DeleteAccount = ({ hideModal }: { hideModal: (data?: any) => void }) => {
  const t = useTranslations('delete_account');
  const dispatch = useAppDispatch();
  const { fbUser, deleteUser } = useAuth();
  const { planIsActive, cancelPlan, planError } = usePlan();
  const [loading, setLoading] = React.useState(false);

  const handleDeleteAccount = async (payload: FormData) => {
    const currentPassword = payload.get('currentPassword') as string;

    try {
      if (fbUser && currentPassword) {
        setLoading(true);
        const credential = EmailAuthProvider.credential(
          fbUser?.email || '',
          currentPassword
        );

        const loggedInUser = await reauthenticateWithCredential(
          fbUser,
          credential
        );
        if (planIsActive) {
          await cancelPlan();
          if (!planError) {
            await deleteUser(loggedInUser.user);
            dispatch(
              showToast({
                text: t('success_with_subscription'),
                variant: 'info',
              })
            );
            hideModal();
          } else {
            dispatch(
              showToast({
                text: t('subscription_cancel_error'),
                variant: 'danger',
              })
            );
            hideModal();
            return;
          }
        } else {
          await deleteUser(loggedInUser.user);
          dispatch(
            showToast({
              text: t('success_message'),
              variant: 'info',
            })
          );
          hideModal();
        }
      }
    } catch (err) {
      console.error(err);
      dispatch(
        showToast({
          text: t('delete_error'),
          variant: 'danger',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <NextUI.Modal
      className="max-w-[500px]"
      classNames={{ content: 'py-8 !px-4 md:!px-8' }}
      defaultOpen={true}
      name={'DeleteAccount'}
    >
      <h2 className="h5 font-semibold text-start text-dark">{t('title')}</h2>
      <p className="text-gray-medium mt-2">{t('warning_message')}</p>

      <p className="text-gray-medium mt-2">{t('subscription_warning')}</p>

      <FormCard
        isAction={false}
        className="mt-8"
        classNames={{
          actionButton: 'max-w-[160px]',
          actionWrapper: 'mt-8 pb-6 inline-flex flex-col items-end p-0',
          formBody: 'p-0',
        }}
        action={handleDeleteAccount}
      >
        <NextUI.Input
          type="password"
          autoComplete="current-password"
          formNoValidate
          variant="outline"
          radius="sm"
          placeholder={t('password_placeholder')}
          name="currentPassword"
        />

        <div className="flex justify-end gap-4 mt-8">
          <Button
            variant="outline"
            className="w-[160px]"
            disabled={loading}
            onClick={() => {
              hideModal();
            }}
          >
            {t('cancel_button')}
          </Button>

          <Button
            className="w-[160px] !bg-danger"
            type="submit"
            isLoading={loading}
          >
            {loading ? '' : t('delete_button')}
          </Button>
        </div>
      </FormCard>
    </NextUI.Modal>
  );
};

export default DeleteAccount;
